import { FC } from 'react'
import { QrCode, Link2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { useTranslation } from '@/hooks/useTranslation'
import Header from '@/components/Header'
import BottomNavigation from '@/components/BottomNavigation'

interface EmptyQRViewProps {
  uuid: string
  project?: {
    uuid: string
    info: { identification?: string; subIdentification?: string }
  } | null
}

const EmptyQRView: FC<EmptyQRViewProps> = ({ uuid, project }) => {
  const navigate = useNavigate()
  const { isInstaller, isAuthenticated } = useAuthStore()
  const { t } = useTranslation()

  const handleLinkToPosition = () => {
    if (project?.uuid && uuid) {
      navigate(`/linking/${project.uuid}?qrTagId=${uuid}`)
    }
  }

  const showLinkingButton = isInstaller && isAuthenticated && project

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header title={`Dashboard - ${uuid}`} />
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[calc(100vh-8rem)]">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg max-w-md w-full">
          <div className="flex flex-col items-center text-center p-8">
            <QrCode className="h-16 w-16 text-[hsl(var(--muted-foreground))] mb-6" />
            <h2 className="text-xl font-semibold mb-2">{t('QR_TAG_EMPTY_TITLE') || 'The QR Tag is Empty'}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
              {showLinkingButton
                ? t('QR_TAG_EMPTY_INSTALLER_DESCRIPTION') || 'This QR tag has no data associated with it yet.'
                : t('QR_TAG_EMPTY_DESCRIPTION') || 'This QR tag has no data associated with it yet.'}
            </p>
            {showLinkingButton && (
              <button
                className="inline-flex items-center gap-2 h-10 px-6 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white font-medium text-sm hover:bg-[hsl(var(--primary))]/90 transition-colors w-full justify-center"
                onClick={handleLinkToPosition}
              >
                <Link2 className="h-4 w-4" />
                {t('LINK_TO_POSITION_BUTTON') || 'Link to Position'}
              </button>
            )}
            {project && (
              <div className="mt-4 p-3 bg-[hsl(var(--muted))] rounded-lg w-full">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {t('PROJECT_INFO') || 'Project'}: {project.info.identification || 'Unknown Project'}
                </p>
                {project.info.subIdentification && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{project.info.subIdentification}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  )
}

export default EmptyQRView
